import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api.js";

export type ViolationKind = "TAB_SWITCH" | "FULLSCREEN_EXIT" | "COPY" | "PASTE" | "BLUR";
export type ShareState =
  | "NOT_ASKED"
  | "GRANTED"
  | "PARTIAL"
  | "DECLINED"
  | "STOPPED"
  | "UNSUPPORTED";

export interface ProctorState {
  violationCount: number;
  lastKind: ViolationKind | null;
  /** What the server did about it: WARN, AUTO_SUBMIT, LOCK, or nothing yet. */
  action: "NONE" | "WARN" | "AUTO_SUBMIT" | "LOCK";
  inFullscreen: boolean;
  /** This browser cannot do fullscreen at all (iPhone Safari, mainly). */
  fullscreenUnsupported: boolean;
}

/**
 * What a browser can and cannot do about exam conduct.
 *
 * It CANNOT prevent a student switching tabs, pressing Cmd-Tab, opening a
 * second window, or reading their phone. No web page can: the operating system
 * owns those, and a page that claims otherwise is lying to the invigilator.
 * Fullscreen can be left at any moment by pressing Escape.
 *
 * It CAN notice. Leaving the page fires `visibilitychange`; leaving the window
 * fires `blur`; leaving fullscreen fires `fullscreenchange`. Each is reported
 * to the server, which owns the count and decides the consequence -- the tally
 * cannot live here, in a page the student controls.
 *
 * Treat this as an attendance record of attention, not a cage.
 */
export function useProctoring({
  examId,
  active,
  requireFullscreen,
  blockCopyPaste,
  captureOnFocusLoss,
  onAutoSubmit,
  onLock,
}: {
  examId: string | undefined;
  /** Only while an attempt is genuinely running. */
  active: boolean;
  requireFullscreen: boolean;
  blockCopyPaste: boolean;
  captureOnFocusLoss: boolean;
  onAutoSubmit: () => void;
  onLock: () => void;
}) {
  const [state, setState] = useState<ProctorState>({
    violationCount: 0,
    lastKind: null,
    action: "NONE",
    inFullscreen: false,
    fullscreenUnsupported: false,
  });

  // One incident often fires two events -- blur and visibilitychange arrive
  // together when a tab is switched. Collapsing them inside a short window
  // keeps one act from costing a student two violations.
  const lastReport = useRef(0);

  /** The shared screen, while the student is sharing it. */
  const stream = useRef<MediaStream | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const [shareState, setShareState] = useState<ShareState>("NOT_ASKED");
  const [shotCount, setShotCount] = useState(0);

  const setShare = useCallback(
    async (next: ShareState) => {
      setShareState(next);
      if (!examId) return;
      try {
        await api.post(`/api/exams/${examId}/screen-share`, { state: next });
      } catch {
        // The attempt may not exist yet; the state is re-sent when it does.
      }
    },
    [examId]
  );

  /**
   * Ask the student to share their screen.
   *
   * There is no way to do this without them choosing, in the browser's own
   * picker, what to share -- and no way to hide that sharing is happening.
   * Must be called straight from a click, like fullscreen.
   */
  const requestScreenShare = useCallback(async (): Promise<ShareState> => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      await setShare("UNSUPPORTED");
      return "UNSUPPORTED";
    }
    try {
      const media = await navigator.mediaDevices.getDisplayMedia({
        // A preference, not a guarantee: the picker decides.
        video: { displaySurface: "monitor" } as MediaTrackConstraints,
        audio: false,
      });
      stream.current = media;

      const el = document.createElement("video");
      el.srcObject = media;
      el.muted = true;
      await el.play().catch(() => {});
      video.current = el;

      // Sharing one tab captures that tab, not what they switch to. Recorded as
      // PARTIAL so nobody mistakes a tab recording for a screen recording.
      const surface = (media.getVideoTracks()[0]?.getSettings() as { displaySurface?: string })
        ?.displaySurface;
      const next: ShareState = surface === "monitor" ? "GRANTED" : "PARTIAL";

      // The browser's own stop control is outside this page; when it is used
      // the track simply ends, and that is worth recording.
      media.getVideoTracks()[0]?.addEventListener("ended", () => {
        stream.current = null;
        void setShare("STOPPED");
      });

      await setShare(next);
      return next;
    } catch {
      await setShare("DECLINED");
      return "DECLINED";
    }
  }, [setShare]);

  /** Grab one frame of the shared screen and send it. */
  const capture = useCallback(
    async (reason: "FOCUS_LOSS" | "TAB_SWITCH" | "FULLSCREEN_EXIT") => {
      if (!examId || !captureOnFocusLoss) return;
      const el = video.current;
      const media = stream.current;
      if (!el || !media || media.getVideoTracks()[0]?.readyState !== "live") return;

      const width = el.videoWidth;
      const height = el.videoHeight;
      if (!width || !height) return;

      // Downscaled and compressed: an exam hall of screenshots at full
      // resolution is a lot of data for evidence that only needs to be legible.
      const scale = Math.min(1, 1280 / width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);

      try {
        await api.post(`/api/exams/${examId}/shots`, {
          reason,
          width: canvas.width,
          height: canvas.height,
          image: canvas.toDataURL("image/jpeg", 0.6),
        });
        setShotCount((n) => n + 1);
      } catch {
        // Evidence is best-effort; never interrupt the exam for it.
      }
    },
    [examId, captureOnFocusLoss]
  );

  const report = useCallback(
    async (kind: ViolationKind) => {
      if (!examId || !active) return;
      const now = Date.now();
      if (now - lastReport.current < 1200) return;
      lastReport.current = now;

      setState((s) => ({ ...s, lastKind: kind, violationCount: s.violationCount + 1 }));
      if (kind === "TAB_SWITCH" || kind === "BLUR" || kind === "FULLSCREEN_EXIT") {
        void capture(kind === "BLUR" ? "FOCUS_LOSS" : kind);
      }
      try {
        const r = await api.post<{
          violationCount: number;
          action: ProctorState["action"];
        }>(`/api/exams/${examId}/violations`, { kind });
        // The server's count is the real one; the optimistic bump above only
        // keeps the banner instant.
        setState((s) => ({ ...s, violationCount: r.violationCount, action: r.action }));
        if (r.action === "AUTO_SUBMIT") onAutoSubmit();
        if (r.action === "LOCK") onLock();
      } catch {
        // A failed report must not interrupt the exam. The student keeps
        // working; the incident is simply not counted.
      }
    },
    [examId, active, onAutoSubmit, onLock, capture]
  );

  /**
   * Enter fullscreen.
   *
   * MUST be called straight from a click, before any `await`. Safari discards
   * the user gesture across an awaited network call and rejects the request --
   * which is exactly how "it doesn't go fullscreen on Safari" happens while
   * Chrome, which is laxer, looks fine.
   */
  const enterFullscreen = useCallback(async () => {
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      // Safari before 16.4 has only the prefixed form.
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else {
        setState((s) => ({ ...s, fullscreenUnsupported: true }));
        return false;
      }
      return true;
    } catch {
      // Refused: no gesture, or the user said no. Not fatal -- the exam runs,
      // and leaving fullscreen is recorded like any other departure.
      return false;
    }
  }, []);

  const isFullscreen = () =>
    !!(document.fullscreenElement || (document as any).webkitFullscreenElement);

  useEffect(() => {
    if (!active) return;

    const onVisibility = () => {
      if (document.hidden) void report("TAB_SWITCH");
    };
    const onBlur = () => void report("BLUR");
    const onFullscreenChange = () => {
      const now = isFullscreen();
      setState((s) => ({ ...s, inFullscreen: now }));
      if (!now && requireFullscreen) void report("FULLSCREEN_EXIT");
    };
    const onCopy = (e: ClipboardEvent) => {
      if (!blockCopyPaste) return;
      e.preventDefault();
      void report("COPY");
    };
    const onPaste = (e: ClipboardEvent) => {
      if (!blockCopyPaste) return;
      e.preventDefault();
      void report("PASTE");
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("paste", onPaste);

    setState((s) => ({ ...s, inFullscreen: isFullscreen() }));

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("paste", onPaste);
    };
  }, [active, requireFullscreen, blockCopyPaste, report]);

  /** Give the screen back. Holding a share after the exam has no purpose. */
  const stopScreenShare = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    video.current = null;
  }, []);

  /** Leave fullscreen quietly when the attempt ends, so the page is usable. */
  const exitFullscreen = useCallback(() => {
    const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> };
    if (!isFullscreen()) return;
    if (doc.exitFullscreen) void doc.exitFullscreen().catch(() => {});
    else if (doc.webkitExitFullscreen) void doc.webkitExitFullscreen();
  }, []);

  return {
    ...state,
    shareState,
    shotCount,
    enterFullscreen,
    exitFullscreen,
    requestScreenShare,
    stopScreenShare,
    dismiss: () => setState((s) => ({ ...s, lastKind: null })),
  };
}
